import React from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { Delete, Payment } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export default function ExpenseDialogs({
  // General Expense
  generalExpenseDialog, setGeneralExpenseDialog, editingGeneralExpense,
  generalExpenseForm, setGeneralExpenseForm, handleSaveGeneralExpense,
  // Invoice
  invoiceDialog, setInvoiceDialog, editingInvoice,
  invoiceForm, setInvoiceForm, handleSaveInvoice,
  // Supplier
  supplierDialog, setSupplierDialog, editingSupplier,
  supplierForm, setSupplierForm, handleSaveSupplier,
  // Recurring
  recurringDialog, setRecurringDialog, editingRecurring,
  recurringForm, setRecurringForm, handleSaveRecurring,
  // Invoice Preview
  invoicePreviewDialog, setInvoicePreviewDialog, invoicePreviewData, invoicePreviewLoading,
  // Invoice Payment
  invoicePaymentDialog, setInvoicePaymentDialog, paymentInvoice,
  invoicePaymentForm, setInvoicePaymentForm, handleSaveInvoicePayment,
  handleCloseInvoicePayment,
  // Expense Payment
  expensePaymentDialog, setExpensePaymentDialog, paymentExpense,
  expensePaymentForm, setExpensePaymentForm, handleSaveExpensePayment,
  handleCloseExpensePayment,
  // Payment History
  paymentHistoryDialog, setPaymentHistoryDialog, paymentHistoryData,
  loadingPaymentHistory, paymentHistoryExpense,
  handleClosePaymentHistory, handleAddPaymentFromHistory, handleDeleteExpensePayment,
  // Data
  categories, suppliers, supplierCategories,
  formatCurrency, navigate, isMobile,
}) {
  return (
    <>
      {/* ========== GENERAL EXPENSE DIALOG ========== */}
      <Dialog open={generalExpenseDialog} onClose={() => setGeneralExpenseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGeneralExpense ? 'Επεξεργασία Εξόδου' : 'Νέο Γενικό Έξοδο'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Κατηγορία</InputLabel>
                <Select
                  value={generalExpenseForm.category_id}
                  onChange={(e) => setGeneralExpenseForm({ ...generalExpenseForm, category_id: e.target.value })}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Ημερομηνία"
                InputLabelProps={{ shrink: true }}
                value={generalExpenseForm.expense_date}
                onChange={(e) => setGeneralExpenseForm({ ...generalExpenseForm, expense_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                label="Καθαρή Αξία (€)"
                value={generalExpenseForm.net_amount}
                onChange={(e) => {
                  const netAmount = parseFloat(e.target.value) || 0;
                  const vatRate = parseFloat(generalExpenseForm.vat_rate) || 0;
                  const vatAmount = (netAmount * vatRate / 100);
                  const totalAmount = netAmount + vatAmount;
                  setGeneralExpenseForm({
                    ...generalExpenseForm,
                    net_amount: e.target.value,
                    vat_amount: vatAmount.toFixed(2),
                    total_amount: totalAmount.toFixed(2)
                  });
                }}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>ΦΠΑ %</InputLabel>
                <Select
                  value={generalExpenseForm.vat_rate}
                  onChange={(e) => {
                    const netAmount = parseFloat(generalExpenseForm.net_amount) || 0;
                    const vatRate = parseFloat(e.target.value) || 0;
                    const vatAmount = (netAmount * vatRate / 100);
                    const totalAmount = netAmount + vatAmount;
                    setGeneralExpenseForm({
                      ...generalExpenseForm,
                      vat_rate: e.target.value,
                      vat_amount: vatAmount.toFixed(2),
                      total_amount: totalAmount.toFixed(2)
                    });
                  }}
                >
                  <MenuItem value={0}>0%</MenuItem>
                  <MenuItem value={6}>6%</MenuItem>
                  <MenuItem value={13}>13%</MenuItem>
                  <MenuItem value={24}>24%</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Σύνολο με ΦΠΑ (€)"
                value={generalExpenseForm.total_amount}
                InputProps={{ readOnly: true }}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Υπολογίζεται αυτόματα"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Περιγραφή"
                value={generalExpenseForm.description}
                onChange={(e) => setGeneralExpenseForm({ ...generalExpenseForm, description: e.target.value })}
              />
            </Grid>
            {/* Payment status is auto-calculated from payments - no manual override */}

            {/* Recurring Expense Section - Only for new expenses */}
            {!editingGeneralExpense && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={generalExpenseForm.is_recurring}
                        onChange={(e) => setGeneralExpenseForm({
                          ...generalExpenseForm,
                          is_recurring: e.target.checked
                        })}
                      />
                    }
                    label="Επαναλαμβανόμενο έξοδο (θα δημιουργείται αυτόματα κάθε μήνα)"
                  />
                </Grid>

                {generalExpenseForm.is_recurring && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Ημέρα Μήνα</InputLabel>
                        <Select
                          value={generalExpenseForm.day_of_month}
                          onChange={(e) => setGeneralExpenseForm({
                            ...generalExpenseForm,
                            day_of_month: e.target.value
                          })}
                        >
                          {[...Array(31)].map((_, i) => (
                            <MenuItem key={i + 1} value={String(i + 1)}>{i + 1}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Τύπος</InputLabel>
                        <Select
                          value={generalExpenseForm.recurring_type}
                          onChange={(e) => setGeneralExpenseForm({
                            ...generalExpenseForm,
                            recurring_type: e.target.value,
                            total_occurrences: e.target.value === 'infinite' ? '' : generalExpenseForm.total_occurrences
                          })}
                        >
                          <MenuItem value="infinite">♾️ Ατελείωτο</MenuItem>
                          <MenuItem value="limited">🔢 Αριθμός Δόσεων</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {generalExpenseForm.recurring_type === 'limited' && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Αριθμός Δόσεων"
                          value={generalExpenseForm.total_occurrences}
                          onChange={(e) => setGeneralExpenseForm({
                            ...generalExpenseForm,
                            total_occurrences: e.target.value
                          })}
                          inputProps={{ min: 1, max: 120 }}
                          helperText="Πόσες φορές θα δημιουργηθεί"
                        />
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeneralExpenseDialog(false)}>Ακύρωση</Button>
          <Button onClick={handleSaveGeneralExpense} variant="contained">Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      {/* ========== INVOICE DIALOG ========== */}
      <Dialog open={invoiceDialog} onClose={() => setInvoiceDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingInvoice ? 'Επεξεργασία Τιμολογίου' : 'Νέο Τιμολόγιο Προμηθευτή'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Προμηθευτής</InputLabel>
                <Select
                  value={invoiceForm.supplier_id}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, supplier_id: e.target.value })}
                >
                  {suppliers.map(sup => (
                    <MenuItem key={sup.id} value={sup.id}>{sup.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Αριθμός Τιμολογίου"
                value={invoiceForm.invoice_number}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Ημερομηνία Τιμολογίου"
                InputLabelProps={{ shrink: true }}
                value={invoiceForm.invoice_date}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                label="Καθαρή Αξία (€)"
                value={invoiceForm.net_amount}
                onChange={(e) => {
                  const netAmount = parseFloat(e.target.value) || 0;
                  const vatRate = parseFloat(invoiceForm.vat_rate) || 0;
                  const vatAmount = (netAmount * vatRate / 100);
                  const totalAmount = netAmount + vatAmount;
                  setInvoiceForm({
                    ...invoiceForm,
                    net_amount: e.target.value,
                    vat_amount: vatAmount.toFixed(2),
                    total_amount: totalAmount.toFixed(2)
                  });
                }}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>ΦΠΑ %</InputLabel>
                <Select
                  value={invoiceForm.vat_rate}
                  onChange={(e) => {
                    const netAmount = parseFloat(invoiceForm.net_amount) || 0;
                    const vatRate = parseFloat(e.target.value) || 0;
                    const vatAmount = (netAmount * vatRate / 100);
                    const totalAmount = netAmount + vatAmount;
                    setInvoiceForm({
                      ...invoiceForm,
                      vat_rate: e.target.value,
                      vat_amount: vatAmount.toFixed(2),
                      total_amount: totalAmount.toFixed(2)
                    });
                  }}
                >
                  <MenuItem value={0}>0%</MenuItem>
                  <MenuItem value={6}>6%</MenuItem>
                  <MenuItem value={13}>13%</MenuItem>
                  <MenuItem value={24}>24%</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Σύνολο με ΦΠΑ (€)"
                value={invoiceForm.total_amount}
                InputProps={{ readOnly: true }}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Υπολογίζεται αυτόματα"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Κατηγορία</InputLabel>
                <Select
                  value={invoiceForm.category_id}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, category_id: e.target.value })}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Όροι Πληρωμής</InputLabel>
                <Select
                  value={invoiceForm.payment_terms}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, payment_terms: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="credit">Επί Πιστώσει</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Payment status is auto-calculated from payments */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Περιγραφή"
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
              />
            </Grid>
            {invoiceForm.payment_terms === 'cash' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Τα τιμολόγια μετρητών δημιουργούν αυτόματα πληρωμή και σημειώνονται ως πληρωμένα
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialog(false)}>Ακύρωση</Button>
          <Button onClick={handleSaveInvoice} variant="contained">Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      {/* ========== SUPPLIER DIALOG ========== */}
      <Dialog open={supplierDialog} onClose={() => setSupplierDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSupplier ? 'Επεξεργασία Προμηθευτή' : 'Νέος Προμηθευτής'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Όνομα"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="ΑΦΜ"
                value={supplierForm.vat_number}
                onChange={(e) => setSupplierForm({ ...supplierForm, vat_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Τηλέφωνο"
                value={supplierForm.phone}
                onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Όροι Πληρωμής</InputLabel>
                <Select
                  value={supplierForm.payment_terms || 'credit'}
                  label="Όροι Πληρωμής"
                  onChange={(e) => setSupplierForm({ ...supplierForm, payment_terms: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="credit">Επί Πιστώσει</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Κατηγορία Προμηθευτή</InputLabel>
                <Select
                  value={supplierForm.category_id || ''}
                  label="Κατηγορία Προμηθευτή"
                  onChange={(e) => setSupplierForm({ ...supplierForm, category_id: e.target.value })}
                >
                  <MenuItem value="">Χωρίς κατηγορία</MenuItem>
                  {supplierCategories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color }} />
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={supplierForm.email}
                onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Διεύθυνση"
                value={supplierForm.address}
                onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Σημειώσεις"
                value={supplierForm.notes}
                onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplierDialog(false)}>Ακύρωση</Button>
          <Button onClick={handleSaveSupplier} variant="contained">Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      {/* ========== RECURRING EXPENSE DIALOG ========== */}
      <Dialog open={recurringDialog} onClose={() => setRecurringDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRecurring ? 'Επεξεργασία Επαναλαμβανόμενου Εξόδου' : 'Νέο Επαναλαμβανόμενο Έξοδο'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Κατηγορία</InputLabel>
                <Select
                  value={recurringForm.category_id}
                  onChange={(e) => setRecurringForm({ ...recurringForm, category_id: e.target.value })}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Ποσό (€)"
                value={recurringForm.amount}
                onChange={(e) => setRecurringForm({ ...recurringForm, amount: e.target.value })}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Ημέρα Μήνα"
                value={recurringForm.day_of_month}
                onChange={(e) => setRecurringForm({ ...recurringForm, day_of_month: e.target.value })}
                inputProps={{ min: '1', max: '31' }}
                helperText="1-31"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Περιγραφή"
                value={recurringForm.description}
                onChange={(e) => setRecurringForm({ ...recurringForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Ημερομηνία Έναρξης"
                InputLabelProps={{ shrink: true }}
                value={recurringForm.start_date}
                onChange={(e) => setRecurringForm({ ...recurringForm, start_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Ημερομηνία Λήξης (προαιρετικό)"
                InputLabelProps={{ shrink: true }}
                value={recurringForm.end_date}
                onChange={(e) => setRecurringForm({ ...recurringForm, end_date: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecurringDialog(false)}>Ακύρωση</Button>
          <Button onClick={handleSaveRecurring} variant="contained">Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Payment Dialog */}
      <Dialog open={invoicePaymentDialog} onClose={handleCloseInvoicePayment} maxWidth="sm" fullWidth>
        <DialogTitle>
          Καταχώρηση Πληρωμής Τιμολογίου
          {paymentInvoice && (
            <Typography variant="body2" color="textSecondary">
              {paymentInvoice.invoice_number} - {paymentInvoice.supplier_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {paymentInvoice && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Συνολικό Ποσό:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    €{paymentInvoice.total_amount?.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Υπόλοιπο:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="error">
                    €{(paymentInvoice.total_amount - (paymentInvoice.paid_amount || 0)).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Ημερομηνία Πληρωμής"
                InputLabelProps={{ shrink: true }}
                value={invoicePaymentForm.payment_date}
                onChange={(e) => setInvoicePaymentForm({ ...invoicePaymentForm, payment_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Ποσό Πληρωμής"
                value={invoicePaymentForm.amount}
                onChange={(e) => setInvoicePaymentForm({ ...invoicePaymentForm, amount: e.target.value })}
                InputProps={{ startAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Τρόπος Πληρωμής</InputLabel>
                <Select
                  value={invoicePaymentForm.payment_method}
                  label="Τρόπος Πληρωμής"
                  onChange={(e) => setInvoicePaymentForm({ ...invoicePaymentForm, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                  <MenuItem value="check">Επιταγή</MenuItem>
                  <MenuItem value="card">Κάρτα</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Αριθμός Αναφοράς"
                value={invoicePaymentForm.reference}
                onChange={(e) => setInvoicePaymentForm({ ...invoicePaymentForm, reference: e.target.value })}
                placeholder="π.χ. αριθμός επιταγής"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Σημειώσεις"
                multiline
                rows={2}
                value={invoicePaymentForm.notes}
                onChange={(e) => setInvoicePaymentForm({ ...invoicePaymentForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvoicePayment}>Ακύρωση</Button>
          <Button onClick={handleSaveInvoicePayment} variant="contained" color="primary">
            Αποθήκευση Πληρωμής
          </Button>
        </DialogActions>
      </Dialog>

      {/* General Expense Payment Dialog */}
      <Dialog open={expensePaymentDialog} onClose={handleCloseExpensePayment} maxWidth="sm" fullWidth>
        <DialogTitle>
          Καταχώρηση Πληρωμής Εξόδου
          {paymentExpense && (
            <Typography variant="body2" color="textSecondary">
              {paymentExpense.category_name} - {paymentExpense.description || 'Χωρίς περιγραφή'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {paymentExpense && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Συνολικό Ποσό:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    €{(paymentExpense.total_amount || paymentExpense.amount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Πληρωμένο:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    €{(paymentExpense.total_paid || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Υπόλοιπο:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="error">
                    €{((paymentExpense.total_amount || paymentExpense.amount || 0) - (paymentExpense.total_paid || 0)).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Ημερομηνία Πληρωμής"
                InputLabelProps={{ shrink: true }}
                value={expensePaymentForm.payment_date}
                onChange={(e) => setExpensePaymentForm({ ...expensePaymentForm, payment_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Ποσό Πληρωμής"
                value={expensePaymentForm.amount}
                onChange={(e) => setExpensePaymentForm({ ...expensePaymentForm, amount: e.target.value })}
                InputProps={{ startAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Τρόπος Πληρωμής</InputLabel>
                <Select
                  value={expensePaymentForm.payment_method}
                  label="Τρόπος Πληρωμής"
                  onChange={(e) => setExpensePaymentForm({ ...expensePaymentForm, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                  <MenuItem value="check">Επιταγή</MenuItem>
                  <MenuItem value="card">Κάρτα</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Αριθμός Αναφοράς"
                value={expensePaymentForm.reference}
                onChange={(e) => setExpensePaymentForm({ ...expensePaymentForm, reference: e.target.value })}
                placeholder="π.χ. αριθμός απόδειξης"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Σημειώσεις"
                multiline
                rows={2}
                value={expensePaymentForm.notes}
                onChange={(e) => setExpensePaymentForm({ ...expensePaymentForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExpensePayment}>Ακύρωση</Button>
          <Button onClick={handleSaveExpensePayment} variant="contained" color="primary">
            Αποθήκευση Πληρωμής
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={paymentHistoryDialog} onClose={handleClosePaymentHistory} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ιστορικό Πληρωμών
          {paymentHistoryExpense && (
            <Typography variant="body2" color="textSecondary">
              {paymentHistoryExpense.category_name} - {paymentHistoryExpense.description || 'Χωρίς περιγραφή'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {paymentHistoryExpense && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Συνολικό:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    €{(paymentHistoryExpense.total_amount || paymentHistoryExpense.amount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Πληρωμένο:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    €{(paymentHistoryExpense.total_paid || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Υπόλοιπο:</Typography>
                  <Typography variant="body1" fontWeight="bold" color={
                    ((paymentHistoryExpense.total_amount || paymentHistoryExpense.amount || 0) - (paymentHistoryExpense.total_paid || 0)) > 0
                      ? "error"
                      : "success.main"
                  }>
                    €{((paymentHistoryExpense.total_amount || paymentHistoryExpense.amount || 0) - (paymentHistoryExpense.total_paid || 0)).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {loadingPaymentHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>Φόρτωση...</Typography>
            </Box>
          ) : paymentHistoryData.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#fafafa', borderRadius: 1 }}>
              <Typography color="textSecondary">Δεν υπάρχουν καταχωρημένες πληρωμές</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ημερομηνία</TableCell>
                    <TableCell>Ποσό</TableCell>
                    <TableCell>Μέθοδος</TableCell>
                    <TableCell>Σημειώσεις</TableCell>
                    <TableCell align="right">Ενέργειες</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistoryData.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.payment_date}</TableCell>
                      <TableCell>
                        <Typography color="success.main" fontWeight="bold">
                          €{(payment.amount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {payment.payment_method === 'cash' && 'Μετρητά'}
                        {payment.payment_method === 'bank_transfer' && 'Τράπεζα'}
                        {payment.payment_method === 'card' && 'Κάρτα'}
                        {payment.payment_method === 'check' && 'Επιταγή'}
                        {!['cash', 'bank_transfer', 'card', 'check'].includes(payment.payment_method) && (payment.payment_method || '-')}
                      </TableCell>
                      <TableCell>{payment.notes || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteExpensePayment(payment.id)}
                          title="Διαγραφή Πληρωμής"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          {paymentHistoryExpense && paymentHistoryExpense.payment_status !== 'paid' && (
            <Button
              onClick={handleAddPaymentFromHistory}
              variant="outlined"
              color="primary"
              startIcon={<Payment />}
            >
              Νέα Πληρωμή
            </Button>
          )}
          <Button onClick={handleClosePaymentHistory}>Κλείσιμο</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog
        open={invoicePreviewDialog}
        onClose={() => setInvoicePreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="h6">
              Τιμολόγιο #{invoicePreviewData?.invoice?.invoice_number}
            </Typography>
            {invoicePreviewData?.invoice?.mydata_mark && (
              <Typography variant="caption" color="text.secondary">
                myDATA: {invoicePreviewData?.invoice?.mydata_mark}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {invoicePreviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : invoicePreviewData ? (
            <>
              {/* Invoice Header Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Προμηθευτής</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {invoicePreviewData.invoice?.supplier_name}
                    </Typography>
                    {invoicePreviewData.invoice?.supplier_vat && (
                      <Typography variant="caption" color="text.secondary">
                        ΑΦΜ: {invoicePreviewData.invoice?.supplier_vat}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Ημερομηνία</Typography>
                    <Typography variant="body1">
                      {invoicePreviewData.invoice?.invoice_date}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Invoice Items */}
              {invoicePreviewData.items && invoicePreviewData.items.length > 0 ? (
                <>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Είδη Τιμολογίου ({invoicePreviewData.items.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell>#</TableCell>
                          <TableCell>Περιγραφή</TableCell>
                          <TableCell align="right">Ποσ.</TableCell>
                          <TableCell align="right">Τιμή</TableCell>
                          <TableCell align="right">ΦΠΑ</TableCell>
                          <TableCell align="right">Σύνολο</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoicePreviewData.items.map((item, index) => (
                          <TableRow key={item.id || index}>
                            <TableCell>{item.line_number || index + 1}</TableCell>
                            <TableCell>{item.description || '-'}</TableCell>
                            <TableCell align="right">
                              {item.quantity} {item.unit_of_measure}
                            </TableCell>
                            <TableCell align="right">€{(item.unit_price || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{item.vat_rate}%</TableCell>
                            <TableCell align="right">€{(item.total_amount || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {invoicePreviewData.invoice?.mydata_mark
                    ? 'Δεν βρέθηκαν είδη για αυτό το τιμολόγιο. Μπορεί το myDATA API να μην επιστρέφει λεπτομέρειες για αυτό το τιμολόγιο.'
                    : 'Αυτό το τιμολόγιο δεν έχει myDATA mark, οπότε δεν μπορούν να φορτωθούν τα είδη.'
                  }
                </Alert>
              )}

              {/* Invoice Totals */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Καθαρή Αξία</Typography>
                    <Typography variant="h6">€{(invoicePreviewData.invoice?.net_amount || 0).toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">ΦΠΑ</Typography>
                    <Typography variant="h6">€{(invoicePreviewData.invoice?.vat_amount || 0).toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Σύνολο</Typography>
                    <Typography variant="h6" color="primary">
                      €{(invoicePreviewData.invoice?.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoicePreviewDialog(false)}>Κλείσιμο</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
